import { UserRoleManager, UserRoles } from "core";
import React from "react";
import ReactDOM from "react-dom";
import {
  ContactPerformerButton,
  isSmallDeviceWidth,
  SlideDown,
  QuestionTooltip,
} from "component-library";
import { UserTypes } from "shared-types";
import { SPOTLIGHT_AGENCY_REF_NUMBER } from "app/core/global-storage-constants";
import { IAgency, IAgencyList } from "models";
import { IAccessibilityContent } from "pages/MainPage/MainPage";
import { profileFallbackTranslations } from "app/store/fallbackTranslations";
import { heapTrackingForAgenciesSection } from "app/helpers/heapTracking";
import { AgencyDetails, IAgencyDetailsContent } from "../AgencyDetails";
import { EditPanel } from "../EditPanel";
import "./AgenciesSection.scss";

export const CONSTANTLY_VISIBLE_MOBILE_ITEMS_COUNT = 3;
const RESERVED_SPACE = 20;

export interface IAgenciesSectionContent extends IAccessibilityContent {
  agenciesSectionName: string;
  agenciesSectionYoungPerformerWithoutAgents: string;
  agenciesSectionShowLess: string;
  agenciesSectionShowMore: string;
  agenciesSectionSchoolPrefix: string;
  agencyDetailsTelephone: string;
  agencyDetailsMobilePhone: string;
  agencyDetailsAlternativeText: string;
  isSelfRepresentedLabel: string;
  isSelfRepresentedContactLabel: string;
  anonIsSelfRepresentedContactLabel: string;
}

export interface IAgenciesSectionProps {
  agencies?: IAgencyList;
  editAgencies: () => void;
  content: IAgenciesSectionContent;
  expandableContainer: React.RefObject<HTMLDivElement>;
  outerContainer: React.RefObject<HTMLDivElement>;
  isExpanded: boolean;
  isReadonly: boolean;
  toggleExpand: () => void;
  isSelfRepresented?: boolean;
  isLookingForRepresentation?: boolean;
  openPerformerContactPopup: (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => void;
  isFilmmakersProfile?: boolean;
}

export class AgenciesSection extends React.Component<IAgenciesSectionProps> {
  private rootRef = React.createRef<HTMLDivElement>();
  private editPanelRef = React.createRef<HTMLDivElement>();
  private expandToggleRef = React.createRef<HTMLAnchorElement>();
  private topItemsRefs: React.RefObject<HTMLDivElement>[] = [];
  private additionalItemsRefs: React.RefObject<HTMLDivElement>[] = [];

  public render() {
    const {
      content,
      editAgencies,
      agencies = { agents: [], schools: [] },
      isReadonly,
    } = this.props;

    const isCastingDirector =
      UserRoleManager.isInRole(UserRoles.castingDirector) ||
      UserRoleManager.isInRole(UserRoles.castingDirectorSearchOnly);

    const isAnon = UserRoleManager.isInRole(UserRoles.anonymous);

    const isSelfRepresented: boolean | undefined =
      this.props.isSelfRepresented &&
      (isAnon || isCastingDirector) &&
      !this.props.isFilmmakersProfile;

    const agenciesDetails = this.getAgenciesDetails(
      agencies,
      !!isSelfRepresented
    );

    const isYoungPerformerWithoutAgents =
      this.isYoungPerformer() && agenciesDetails.length === 0;

    const isAgent = UserRoleManager.isInRole(UserRoles.agent);

    const isEditHidden = isAgent || isYoungPerformerWithoutAgents || isReadonly;

    return (
      <section
        ref={this.rootRef}
        className="c-agencies-section"
        aria-label={content.agencyDetailsAlternativeText}
      >
        <div ref={this.editPanelRef} className="c-agencies-section__edit-panel">
          <EditPanel
            header={content.agenciesSectionName}
            editFn={() => {
              heapTrackingForAgenciesSection();

              editAgencies();
            }}
            hideEdit={isEditHidden}
            editLinkAltText={content.editLinkAltText}
          />
        </div>
        {isYoungPerformerWithoutAgents
          ? this.renderPlaceholder(
              content.agenciesSectionYoungPerformerWithoutAgents
            )
          : this.renderContent(agenciesDetails, !!isSelfRepresented, isAnon)}
      </section>
    );
  }

  public componentDidMount() {
    window.addEventListener("resize", this.checkVisibility, false);
  }

  public componentDidUpdate() {
    this.checkVisibility();
  }

  private getAgenciesDetails = (
    agencies: Omit<
      IAgencyList,
      "isSelfRepresented" | "isLookingForRepresentation"
    >,
    isSelfRepresented: boolean
  ) => {
    const { content } = this.props;

    const result: IAgency[] = [];
    const userType = UserRoleManager.getUserType();

    if (!isSelfRepresented) {
      result.push(...agencies.agents);
    } else {
      const additionalAgencies = agencies.agents.filter(function (obj) {
        return obj.refNumber !== SPOTLIGHT_AGENCY_REF_NUMBER;
      });

      result.push(...additionalAgencies);
    }

    if (
      agencies.schools &&
      [UserTypes.femaleGraduates, UserTypes.maleGraduates].some(
        (type) => type === userType
      )
    ) {
      const schools = [...agencies.schools].map((school) => ({
        ...school,
        name: content.agenciesSectionSchoolPrefix.toString() + school.name,
      }));

      result.push(...schools);
    }

    return result;
  };

  private isYoungPerformer = () => {
    const userType = UserRoleManager.getUserType();
    const isYoungPerformer = [
      UserTypes.youngPerformerFemale,
      UserTypes.youngPerformerMale,
    ].some((type) => type === userType);

    return isYoungPerformer;
  };

  private renderPlaceholder = (text: string) => (
    <div className="c-agencies-section__placeholder-text">{text}</div>
  );

  private renderIsLookingForRepresentation = () => {
    const { isLookingForRepresentation } = this.props;

    if (!isLookingForRepresentation) return;

    return (
      <div className="c-agency-details">
        <div className="c-agency-details__primary-text">
          <h5 className="c-agency-details__agency-name">
            {this.props.content.isSelfRepresentedLabel}
          </h5>
        </div>
        <div className="flex items-center gap-[5px]">
          {profileFallbackTranslations.seekRepresentation}
          <div>
            <QuestionTooltip
              title={profileFallbackTranslations.seekRepresentation}
              html={profileFallbackTranslations.seekRepresentationToolTip}
            />
          </div>
        </div>
        <ContactPerformerButton
          onClick={this.props.openPerformerContactPopup}
          iconButton
        />
      </div>
    );
  };

  private renderContent = (
    agencies: IAgency[],
    isSelfRepresented: boolean,
    isAnon: boolean
  ) => {
    const { expandableContainer, content, isExpanded } = this.props;

    const anonContactLabel = content.anonIsSelfRepresentedContactLabel;
    const anonContactLabelHref = `mailto:${content.anonIsSelfRepresentedContactLabel}`;

    const agencyDetailsContent: IAgencyDetailsContent = {
      mobilePhoneText: content.agencyDetailsMobilePhone,
      telephoneText: content.agencyDetailsTelephone,
    };

    this.topItemsRefs = [];

    return (
      <React.Fragment>
        {isSelfRepresented && (
          <div className="c-agency-details">
            <div className="c-agency-details__primary-text">
              <h5 className="c-agency-details__agency-name">
                {content.isSelfRepresentedLabel}
              </h5>
            </div>
            <p className="c-agency-details__secondary-text">
              {isAnon ? (
                <a
                  href={anonContactLabelHref}
                  className="g-alternative-link g-alternative-link--md"
                >
                  {anonContactLabel}
                </a>
              ) : (
                <ContactPerformerButton
                  onClick={this.props.openPerformerContactPopup}
                  iconButton
                />
              )}
            </p>
          </div>
        )}
        {agencies.map((agency, index) => {
          const agencyRef = React.createRef<HTMLDivElement>();
          this.topItemsRefs.push(agencyRef);
          const isSLAgency = agency.refNumber === SPOTLIGHT_AGENCY_REF_NUMBER;
          const isLookingForRepresentation =
            this.props.isLookingForRepresentation;
          const isAgentUser = UserRoleManager.isInRole(UserRoles.agent);

          return (
            <div
              key={agency.name + index}
              ref={agencyRef}
              className="c-agencies-section__top-row"
            >
              {isSLAgency && isLookingForRepresentation && isAgentUser ? (
                this.renderIsLookingForRepresentation()
              ) : (
                <AgencyDetails agency={agency} content={agencyDetailsContent} />
              )}
            </div>
          );
        })}
        {expandableContainer.current &&
          this.renderHiddenItems(
            agencies,
            agencyDetailsContent,
            expandableContainer.current,
            isExpanded
          )}
        {this.renderExpandToggle(content, isExpanded)}
      </React.Fragment>
    );
  };

  private renderHiddenItems = (
    agencies: IAgency[],
    content: IAgencyDetailsContent,
    container: HTMLDivElement,
    isExpanded: boolean
  ) => {
    this.additionalItemsRefs = [];

    return ReactDOM.createPortal(
      <SlideDown
        className="c-agencies-section__expandable-area"
        closed={!isExpanded}
        transitionOnAppear={false}
      >
        <div className="c-agencies-section__details-list">
          {agencies.map((agency, index) => {
            const agencyRef = React.createRef<HTMLDivElement>();
            this.additionalItemsRefs.push(agencyRef);

            return (
              <div
                key={agency.name + index}
                ref={agencyRef}
                className="c-agencies-section__details"
              >
                <AgencyDetails agency={agency} content={content} />
              </div>
            );
          })}
        </div>
      </SlideDown>,
      container
    );
  };

  private renderExpandToggle = (
    content: IAgenciesSectionContent,
    isExpanded: boolean
  ) => (
    <a
      ref={this.expandToggleRef}
      className="c-agencies-section__expand-toggle g-alternative-link g-alternative-link--md"
      onClick={this.props.toggleExpand}
      tabIndex={0}
    >
      {isExpanded
        ? content.agenciesSectionShowLess
        : content.agenciesSectionShowMore}
    </a>
  );

  private checkVisibility = () => {
    const {
      outerContainer: { current: outerContainer },
    } = this.props;
    const { current: root } = this.rootRef;
    const { current: editPanel } = this.editPanelRef;

    if (root && outerContainer && editPanel) {
      const { clientHeight: maxHeight = 0 } = outerContainer;
      const { clientHeight: editPanelHeight = 0 } = editPanel;

      const hideTopRow = (
        topRow: Element | null,
        hiddenRow: Element | null
      ) => {
        topRow?.classList.add("hidden");
        hiddenRow?.classList.remove("hidden");
      };

      const showTopRow = (
        topRow: Element | null,
        hiddenRow: Element | null
      ) => {
        topRow?.classList.remove("hidden");
        hiddenRow?.classList.add("hidden");
      };

      let hasHiddenElements = false;

      if (!isSmallDeviceWidth()) {
        let heightLeft = maxHeight - editPanelHeight - RESERVED_SPACE;

        this.topItemsRefs.forEach(({ current: topRow }, index) => {
          if (topRow) {
            heightLeft -= topRow.scrollHeight;

            const { current: hiddenRow } = this.additionalItemsRefs[index];

            if (heightLeft < 0) {
              hideTopRow(topRow, hiddenRow);
              hasHiddenElements = true;
            } else {
              showTopRow(topRow, hiddenRow);
            }
          }
        });
      } else {
        this.topItemsRefs.forEach(({ current: topRow }, index) => {
          const { current: hiddenRow } = this.additionalItemsRefs[index];

          if (index < CONSTANTLY_VISIBLE_MOBILE_ITEMS_COUNT) {
            showTopRow(topRow, hiddenRow);
          } else {
            hideTopRow(topRow, hiddenRow);
            hasHiddenElements = true;
          }
        });
      }

      const { current: expandToggle } = this.expandToggleRef;

      if (expandToggle) {
        if (!hasHiddenElements) {
          expandToggle.classList.add("hidden");
        } else {
          expandToggle.classList.remove("hidden");
        }
      }
    }
  };
}

export default AgenciesSection;
